import { FormTextField } from 'components/form';
import { Button, Center, Spacer, toast, Typo } from 'components/ui-kit';
import { Form, Formik, FormikProps } from 'formik';
import { TContactRequest } from 'lib';
import { EMAIL_REGEXP } from 'lib/validators/validators';
import Link from 'next/link';
import React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import * as yup from 'yup';
import styles from './contact-form.module.scss';
import { contactService } from '../../services';

type TContactForm = TContactRequest & { recaptcha: string | null };

const initialValues: TContactForm = {
  name: '',
  email: '',
  comment: '',
  subject: '',
  recaptcha: null,
};

const validationSchema = yup.object().shape({
  name: yup.string().required(),
  email: yup
    .string()
    .email()
    .matches(EMAIL_REGEXP, 'Проверьте правильность ввода email')
    .required(),
  comment: yup.string().required(),
  subject: yup.string().required(),
  recaptcha: yup.string().required('Подтвердите, что вы не робот').nullable(),
});

type TContactFormProps = {
  onSubmit(isSuccess: boolean): void;
};

export const ContactForm = ({ onSubmit }: TContactFormProps): JSX.Element => {
  const recaptchaRef = React.createRef<ReCAPTCHA>();

  const handleSubmit = (value: TContactRequest): void => {
    const captchaCode = recaptchaRef.current?.getValue();
    if (!captchaCode) {
      toast.error('Пройдите проверку');
      return;
    }

    contactService
      .sendQuestion(value)
      .then(onSubmit)
      .catch(() => toast.error('Ошибка при отправке'));
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({
        handleSubmit: handleFormikSubmit,
        errors,
        isSubmitting,
        isValid,
        setFieldValue,
        setSubmitting,
      }: FormikProps<TContactForm>) => (
        <Form placeholder="" onSubmit={handleFormikSubmit}>
          <FormTextField label="Ваше имя" name="name" />
          <Spacer size={24} />
          <FormTextField label="Email" name="email" type="email" />
          <Spacer size={24} />
          <FormTextField label="Тема обращения" name="subject" />
          <Spacer size={24} />
          <FormTextField label="Ваш вопрос или предложение" name="comment" />
          <Spacer size={32} />
          {!!errors && !!errors.recaptcha && (
            <Center>
              <Spacer size={8} />
              <Typo color="alert" variant="xm">
                {errors.recaptcha}
              </Typo>
            </Center>
          )}
          <ReCAPTCHA
            sitekey={process.env.captchaSiteKey}
            ref={recaptchaRef}
            className={styles.captcha}
            onChange={(value: string | null) => {
              setFieldValue('recaptcha', value);
              setSubmitting(false);
            }}
          />
          <Spacer size={24} />
          <Center>
            <Button
              variant="primary"
              type="submit"
              isDisabled={!isValid || isSubmitting}
            >
              Отправить
            </Button>
          </Center>
          <Spacer size={32} />
          <Typo variant="m" align="center">
            Отправляя сообщение, вы&nbsp;соглашаетесь <br /> на&nbsp;
            <Link href="/concent" target="_blank">
              условия обработки персональных данных
            </Link>{' '}
            и&nbsp;
            <Link href="/privacy" target="_blank">
              политики конфиденциальности
            </Link>
            .
          </Typo>
        </Form>
      )}
    </Formik>
  );
};
